/**
 * TouchInput — a interface OFICIAL do VOID HUNTER (mobile-first).
 * O ataque é AUTOMÁTICO: a HUD tem apenas joystick (mover), HOOK e CORRER (sprint),
 * mais um botão contextual (USAR) e o pause. Joystick virtual dinâmico à esquerda;
 * botões grandes à direita, no alcance do polegar, respeitando safe areas.
 * Pointer Events (multi-touch). Bloqueia scroll/zoom/gestos do navegador.
 */
import { i18n } from "../i18n/i18n";

/** 10.6F — raio de captura do joystick, em múltiplos do raio da base. 1.25 dá folga para o
 *  polegar sem transformar o canto da tela num direcional. */
const GRAB_FACTOR = 1.25;

interface HoldButton {
  el: HTMLButtonElement;
  held: boolean;
  edge: boolean;
  pointerId: number; // ponteiro que está pressionando (-1 = nenhum)
}

/** 10.9CF — ÍCONES das magias (SVG inline, 1 cor = currentColor): reconhecíveis no tamanho do botão. */
const SPELL_ICONS: Record<string, string> = {
  // Golpe Sísmico: punho/martelo caindo com ondas no chão
  warrior: `<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M24 6v18"/><path d="M15 12l9 12 9-12"/><path d="M8 34c4-4 8-4 12 0s8 4 12 0 8-4 8 0"/><path d="M10 41c4-3 7-3 11 0s7 3 11 0 7-3 8 0"/></svg>`,
  // Chuva de Flechas: 3 flechas em leque descendo
  archer: `<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M24 6v30"/><path d="M18 30l6 8 6-8"/><path d="M11 10l3 26"/><path d="M8 30l6 6 3-8"/><path d="M37 10l-3 26"/><path d="M40 30l-6 6-3-8"/></svg>`,
  // Nova Arcana: núcleo com anel e raios
  mage: `<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><circle cx="24" cy="24" r="6" fill="currentColor" stroke="none"/><circle cx="24" cy="24" r="13" stroke-dasharray="6 5"/><path d="M24 3v6M24 39v6M3 24h6M39 24h6M9 9l4 4M35 35l4 4M39 9l-4 4M9 39l4-4"/></svg>`,
  // Exército do Vazio: caveira com dois espectros
  necromancer: `<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M14 20a10 10 0 0 1 20 0v7h-3v5h-3v-4h-8v4h-3v-5h-3z"/><circle cx="20" cy="21" r="2" fill="currentColor" stroke="none"/><circle cx="28" cy="21" r="2" fill="currentColor" stroke="none"/><path d="M6 44c0-6 3-9 5-9s3 3 3 7"/><path d="M42 44c0-6-3-9-5-9s-3 3-3 7"/></svg>`,
};

export class TouchInput {
  readonly move = { x: 0, y: 0 };
  usingTouch = false;

  private enabled = false;
  private readonly root: HTMLDivElement;

  // joystick
  private readonly stickZone: HTMLDivElement;
  private readonly stickBase: HTMLDivElement;
  private readonly stickKnob: HTMLDivElement;
  private stickPointer: number | null = null;
  private readonly baseXY = { x: 0, y: 0 };
  private readonly maxRadius = 52;

  // botões (SEM ataque — o ataque é automático)
  private readonly hook: HoldButton;
  private readonly sprint: HoldButton;
  private readonly spell: HoldButton; // 10.9U — magia da classe
  private readonly spell2: HoldButton; // 10.9CX
  private readonly spell3: HoldButton; // 10.9CX
  private readonly interact: HoldButton;
  private readonly pauseBtn: HoldButton;
  private readonly hookCd: HTMLDivElement;
  private readonly sprintCd: HTMLDivElement;
  private readonly spellCd: HTMLDivElement; // 10.9U
  private readonly spell2Cd: HTMLDivElement; // 10.9CX
  private readonly spell3Cd: HTMLDivElement; // 10.9CX

  constructor(uiRoot: HTMLElement) {
    this.root = document.createElement("div");
    this.root.className = "touch-controls";
    this.root.hidden = true;
    this.root.innerHTML = `
      <button class="tbtn pause" data-a="pause">II</button>
      <div class="stick-zone">
        <div class="stick-base"><div class="stick-knob"></div></div>
      </div>
      <div class="touch-buttons">
        <button class="tbtn sprint" data-a="sprint"><span></span><div class="tcd"></div></button>
        <button class="tbtn hook" data-a="hook"><span></span><div class="tcd"></div></button>
        <button class="tbtn spell spell3" data-a="spell3"><span></span><div class="tcd"></div></button>
        <button class="tbtn spell spell2" data-a="spell2"><span></span><div class="tcd"></div></button>
        <button class="tbtn spell" data-a="spell"><span></span><div class="tcd"></div></button>
        <button class="tbtn interact" data-a="interact"><span></span></button>
      </div>`;
    uiRoot.appendChild(this.root);

    this.stickZone = this.root.querySelector(".stick-zone")!;
    this.stickBase = this.root.querySelector(".stick-base")!;
    this.stickKnob = this.root.querySelector(".stick-knob")!;
    this.hook = this.button("hook");
    this.sprint = this.button("sprint");
    this.spell = this.button("spell"); // 10.9U
    this.spell2 = this.button("spell2"); // 10.9CX
    this.spell3 = this.button("spell3"); // 10.9CX
    // 10.9CX — empilha as magias 2/3 acima da principal via estilo inline (vence a cascata do CSS)
    this.spell2.el.style.setProperty("bottom", "calc(var(--safe-bottom) + 20px + var(--touch-size) * 1.4 + 12px + (var(--touch-size) * 0.95 + 10px))", "important");
    this.spell3.el.style.setProperty("bottom", "calc(var(--safe-bottom) + 20px + var(--touch-size) * 1.4 + 12px + (var(--touch-size) * 0.95 + 10px) * 2)", "important");
    this.interact = this.button("interact");
    this.pauseBtn = this.button("pause");
    this.hookCd = this.hook.el.querySelector(".tcd")!;
    this.sprintCd = this.sprint.el.querySelector(".tcd")!;
    this.spellCd = this.spell.el.querySelector(".tcd")!; // 10.9U
    this.spell2Cd = this.spell2.el.querySelector(".tcd")!; // 10.9CX
    this.spell3Cd = this.spell3.el.querySelector(".tcd")!; // 10.9CX

    this.renderLabels();
    i18n.onChange(() => this.renderLabels());

    this.bindStick();
    this.bindButtons();
    // 10.9FU — REDE DE SEGURANÇA contra toque "travado": se o evento de SOLTAR se perde (comum no
    // multitoque quando o dedo sai do botão), o botão/joystick ficava preso. Aqui, no pointerup/cancel
    // GLOBAL, solto qualquer controle cujo ponteiro terminou — nenhum toque fica grudado.
    const globalRelease = (e: PointerEvent): void => {
      if (this.stickPointer === e.pointerId) {
        this.stickPointer = null; this.move.x = 0; this.move.y = 0;
        this.stickBase.classList.remove("active"); this.moveKnob(0, 0);
      }
      for (const b of [this.hook, this.sprint, this.spell, this.spell2, this.spell3, this.interact, this.pauseBtn]) {
        if (b.pointerId === e.pointerId) { b.held = false; b.pointerId = -1; b.el.classList.remove("pressed"); }
      }
    };
    window.addEventListener("pointerup", globalRelease);
    window.addEventListener("pointercancel", globalRelease);
    this.root.addEventListener("touchmove", (e) => e.preventDefault(), { passive: false });
    this.root.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  private button(a: string): HoldButton {
    const el = this.root.querySelector<HTMLButtonElement>(`.tbtn[data-a="${a}"]`)!;
    return { el, held: false, edge: false, pointerId: -1 };
  }

  private renderLabels(): void {
    // §11: icon-first (VOID style). O nome localizado vai no aria-label (acessibilidade).
    this.hook.el.querySelector("span")!.innerHTML = `<svg viewBox="0 0 32 32" width="38" height="38" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="19" cy="5" r="3"/><path d="M19 8v13a8 8 0 0 1-16 0v-6l5 5"/></svg>`;
    this.sprint.el.querySelector("span")!.textContent = "»";
    if (!this.spell.el.querySelector("span")!.innerHTML) this.spell.el.querySelector("span")!.innerHTML = SPELL_ICONS.mage; // 10.9U/CF — ícone da magia (troca por classe em setSpellIcon)
    this.interact.el.querySelector("span")!.textContent = "✦";
    this.hook.el.setAttribute("aria-label", i18n.t("touch.hook"));
    this.sprint.el.setAttribute("aria-label", i18n.t("touch.sprint"));
    this.spell.el.setAttribute("aria-label", i18n.t("touch.spell"));
    this.interact.el.setAttribute("aria-label", i18n.t("touch.interact"));
  }

  // ---- joystick de BASE FIXA (§2-§5): base sempre visível; knob move relativo ao centro dela ----
  /** Lê o centro atual da base fixa (robusto a qualquer CSS/viewport). */
  private readBaseCenter(): void {
    const r = this.stickBase.getBoundingClientRect();
    this.baseXY.x = r.left + r.width / 2;
    this.baseXY.y = r.top + r.height / 2;
  }
  private bindStick(): void {
    this.stickZone.addEventListener("pointerdown", (e) => {
      if (!this.enabled || this.stickPointer !== null) return;
      // 10.6F — só pega se o dedo caiu PERTO da base. Antes bastava tocar em qualquer ponto
      // da zona (que era enorme) e o Caçador saía andando sozinho.
      this.readBaseCenter();
      const r = this.stickBase.getBoundingClientRect();
      const grab = (r.width / 2) * GRAB_FACTOR;
      if (Math.hypot(e.clientX - this.baseXY.x, e.clientY - this.baseXY.y) > grab) return;
      this.usingTouch = true; // controles na tela usam assistência mesmo com mouse
      this.stickPointer = e.pointerId;
      try {
        this.stickZone.setPointerCapture(e.pointerId);
      } catch {
        /* ponteiro já liberado */
      }
      this.stickBase.classList.add("active");
      this.applyStick(e.clientX, e.clientY);
    });
    this.stickZone.addEventListener("pointermove", (e) => {
      if (e.pointerId !== this.stickPointer) return;
      this.applyStick(e.clientX, e.clientY);
    });
    const end = (e: PointerEvent): void => {
      if (e.pointerId !== this.stickPointer) return;
      this.stickPointer = null;
      this.move.x = 0;
      this.move.y = 0;
      this.stickBase.classList.remove("active");
      this.moveKnob(0, 0); // knob retorna ao centro; a base PERMANECE visível
    };
    this.stickZone.addEventListener("pointerup", end);
    this.stickZone.addEventListener("pointercancel", end);
    this.stickZone.addEventListener("lostpointercapture", end);
  }

  private applyStick(clientX: number, clientY: number): void {
    let dx = clientX - this.baseXY.x;
    let dy = clientY - this.baseXY.y;
    const len = Math.hypot(dx, dy);
    if (len > this.maxRadius) {
      dx = (dx / len) * this.maxRadius;
      dy = (dy / len) * this.maxRadius;
    }
    this.move.x = dx / this.maxRadius;
    this.move.y = dy / this.maxRadius;
    this.moveKnob(dx, dy);
  }

  private moveKnob(dx: number, dy: number): void {
    this.stickKnob.style.transform = `translate(${dx}px, ${dy}px)`;
  }

  // ---- botões ----
  private bindButtons(): void {
    for (const b of [this.hook, this.sprint, this.spell, this.spell2, this.spell3, this.interact, this.pauseBtn]) {
      b.el.addEventListener("pointerdown", (e) => {
        if (!this.enabled) return;
        this.usingTouch = true;
        e.preventDefault();
        b.edge = true; // toque sempre registra a borda
        if (!b.held) {
          // primeiro dedo vira o "dono" do held; dedos extras só registram a borda
          b.held = true;
          b.pointerId = e.pointerId;
          try {
            b.el.setPointerCapture(e.pointerId);
          } catch {
            /* ponteiro já liberado */
          }
          b.el.classList.add("pressed");
        }
      });
      const release = (e: PointerEvent): void => {
        if (e.pointerId !== b.pointerId) return; // só o dono solta o held
        b.held = false;
        b.pointerId = -1;
        b.el.classList.remove("pressed");
        try {
          b.el.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      };
      b.el.addEventListener("pointerup", release);
      b.el.addEventListener("pointercancel", release);
    }
  }

  // ---- API abstrata ----
  hookPressed(): boolean {
    return this.enabled && this.hook.edge;
  }
  sprintPressed(): boolean {
    return this.enabled && this.sprint.edge;
  }
  spellPressed(): boolean {
    return this.enabled && this.spell.edge;
  }
  spell2Pressed(): boolean { return this.enabled && this.spell2.edge; } // 10.9CX
  spell3Pressed(): boolean { return this.enabled && this.spell3.edge; } // 10.9CX
  interactPressed(): boolean {
    return this.enabled && this.interact.edge;
  }
  pausePressed(): boolean {
    return this.pauseBtn.edge;
  }

  /** Visual de recarga RADIAL (0 = pronto, 1 = acabou de usar). */
  setHookCooldown(frac: number): void {
    this.hookCd.style.setProperty("--cd", String(Math.max(0, Math.min(1, frac))));
  }
  setSprintCooldown(frac: number): void {
    this.sprintCd.style.setProperty("--cd", String(Math.max(0, Math.min(1, frac))));
  }
  setSpellCooldown(frac: number): void {
    this.spellCd.style.setProperty("--cd", String(Math.max(0, Math.min(1, frac))));
  }
  setSpellSlotCooldown(slot: number, frac: number): void { // 10.9CX
    const el = slot === 1 ? this.spell2Cd : slot === 2 ? this.spell3Cd : this.spellCd;
    el.style.setProperty("--cd", String(Math.max(0, Math.min(1, frac))));
  }
  setSpellSlotIcon(slot: number, iconHtml: string, colorHex: string): void { // 10.9CX
    const b = slot === 0 ? this.spell : slot === 1 ? this.spell2 : this.spell3; // 10.9FC — slot 0 tambem troca
    b.el.querySelector("span")!.innerHTML = iconHtml;
    b.el.style.setProperty("--spell", colorHex);
  }
  setSpellSlotVisible(slot: number, v: boolean): void { // 10.9CX
    const b = slot === 1 ? this.spell2 : this.spell3;
    b.el.classList.toggle("hidden-btn", !v);
  }
  /** 10.9CF — ícone e cor da magia da classe ativa. */
  setSpellIcon(classId: string, colorHex: string): void {
    const span = this.spell.el.querySelector("span")!;
    span.innerHTML = SPELL_ICONS[classId] ?? SPELL_ICONS.mage;
    this.spell.el.style.setProperty("--spell", colorHex);
  }
  /** 10.9U — mostra o botão de magia só quando a classe tem magia. */
  setSpellVisible(v: boolean): void {
    this.spell.el.classList.toggle("hidden-btn", !v);
  }
  /** Mostra/oculta o botão de interação contextual. */
  setInteractVisible(v: boolean): void {
    this.interact.el.classList.toggle("visible", v);
  }

  /**
   * HOOK desabilitado (safe zone / buffer): apenas muda o estado visual (dim), sem
   * remover/recriar o DOM. O alvo de toque continua o mesmo (o toque mostra "SAFE ZONE").
   */
  setHookDisabled(v: boolean): void {
    this.hook.el.classList.toggle("disabled", v);
    this.hook.el.setAttribute("aria-disabled", v ? "true" : "false");
  }

  /** Pulsa o botão HOOK (oportunidade de Hook, ex.: placa exposta do Colossus). */
  setHookPulse(v: boolean): void {
    this.hook.el.classList.toggle("pulse", v);
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
    this.root.hidden = !v;
    if (!v) {
      this.move.x = 0;
      this.move.y = 0;
      this.stickPointer = null;
      this.stickBase.classList.remove("active");
      this.moveKnob(0, 0);
      for (const b of [this.hook, this.sprint, this.spell, this.spell2, this.spell3, this.interact, this.pauseBtn]) {
        b.held = false;
        b.edge = false;
        b.pointerId = -1;
        b.el.classList.remove("pressed");
      }
    }
  }

  update(): void {
    this.hook.edge = false;
    this.sprint.edge = false;
    this.spell.edge = false;
    this.spell2.edge = false;
    this.spell3.edge = false;
    this.interact.edge = false;
    this.pauseBtn.edge = false;
  }
}
